import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import * as Router from "next/router";
import axios from "axios";
import DOMPurify from "dompurify";

import * as u from "@/utils";
import contactTypes from "@/utils/contactTypes";
import * as gtag from "@/utils/gtag";
import * as T from "@/components/Typography";
import * as UserContext from "@/components/UserContext";
import RxButton from "@/components/ui/inputs/RxButton";
import RxDropdownSelect from "@/components/ui/inputs/RxDropdownSelect";
import RxTextInput from "@/components/ui/inputs/RxTextInput";
import RxTextArea from "@/components/ui/inputs/RxTextArea";

const RxContactForm = () => {
  const userInfo = React.useContext(UserContext.Context);
  const router = Router.useRouter();

  const reasons = useMemo(() => Object.keys(contactTypes), []);

  const [name, setName] = React.useState(userInfo?.name || "");
  const [email, setEmail] = React.useState(userInfo?.email || "");
  const [type, setType] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(false);

  const onSelectQuery = useCallback(
    (e) => {
      if (e.target.value === "Private Hire Events") {
        return router.replace(
          "https://allbright.tripleseat.com/party_request/24934"
        );
      }
      setType(e.target.value);
    },
    [email, name, message, type]
  );

  const handleSubmit = useCallback(
    async (ev) => {
      ev.preventDefault();

      setSuccess(false);
      setError(null);

      try {
        const url = "/api/user.contact-allbright";
        const userType = !userInfo?.id
          ? "logged out"
          : userInfo?.isClub
            ? "club"
            : userInfo?.isPaid
              ? "plus"
              : "registered";
        const body = {
          name: name,
          email: email,
          phone: userInfo?.phoneNumber,
          type: type,
          message: DOMPurify.sanitize(message),
          source: `${userType} user on web`,
        };

        await axios.post(url, body);

        gtag.event({
          name: gtag.GA_EVENT.ACCOUNT_ENGAGEMENT,
          category: "User",
          label: "Contact Submit",
          value: type,
        });

        setName(userInfo?.name || "");
        setEmail(userInfo?.email || "");
        setType("");
        setMessage("");
        setSuccess(true);
      } catch (err) {
        setError(err);
      }
    },
    [email, name, message, type]
  );

  return (
    <S.Container>
      <S.Form onSubmit={handleSubmit}>
        <T.H4>Get in touch with us</T.H4>
        <TT.Text>We are here to help you</TT.Text>

        {error && (
          <S.Error>
            There was a problem sending your message, email us directly at
            digital@allbright.co
          </S.Error>
        )}
        {success && (
          <S.Success>
            <b>Thanks for getting in touch!</b> A member of our team will review
            your enquiry and get back to you as soon as possible.
          </S.Success>
        )}

        <S.FormControl>
          <RxTextInput
            labelText="Name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </S.FormControl>

        <S.FormControl>
          <RxTextInput
            labelText="Email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </S.FormControl>

        <S.FormControl>
          <RxDropdownSelect
            name="reason"
            labelText="Reason for contact"
            placeholder="Reason for contact"
            selected={type}
            onChange={(e) => onSelectQuery(e)}
            options={reasons}
          />
        </S.FormControl>

        <S.FormControl>
          <RxTextArea
            labelText="Message"
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </S.FormControl>

        <S.RxButton
          disabled={!type}
          buttonText="Submit enquiry"
          type="submit"
        />
      </S.Form>
    </S.Container>
  );
};

const S = () => {};

S.Container = styled.div`
  width: 100%;
  padding-top: 20px;
`;

S.Form = styled.form`
  max-width: 600px;
`;

S.FormControl = styled.div`
  margin-bottom: 20px;
`;

S.RxButton = styled(RxButton)`
  margin: 10px auto 0 auto;
  max-width: 100%;
`;

S.Error = styled.div`
  margin-bottom: 20px;
  color: ${u.colours.errorRed};
`;

S.Success = styled.div`
  margin-bottom: 20px;
  color: ${u.colours.turquoise};
`;

const TT = () => {};

TT.Text = styled(T.CL)`
  margin: 20px 0;
  color: ${u.colours.charcoal};
`;

export default RxContactForm;
